<script setup lang="ts">
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import type { PromotionCode } from "@/rvapi";
import { inXDays, local2utc } from "@/utils";
import { usePromotionStore } from "@/stores/promotions";
const route = useRoute();
const showType = route.query?.type !== undefined;
const isCodeInputError = ref(false);
const noCodeError = ref("");
const promotions = usePromotionStore();

const emit = defineEmits<{
  (e: "codeCreated", code: PromotionCode): void;
  (e: "codeCreationFailed"): void;
}>();
const campaignType = ref("free_card");
const requireLogin = ref(true);
const codetext = ref("");
const campaignlimit = ref("10000");
const campaignexpire = ref(inXDays(30));
const campaignexpiret = ref("");
const freecardValue = ref("1");
const percentValue = ref("10");
const uploading = ref(false);
const isDateInvalid = ref(false);
const dateErrorMessage = ref("");

const percentError = computed(() => {
  if (!/^\d+$/.test(percentValue.value)) {
    return "invalid number";
  }
  const test = parseInt(percentValue.value, 10);
  if (test < 1) {
    return "value must be > 0";
  }
  if (test > 100) {
    return "value must be < 100";
  }
  return "";
});
const campaignValue = computed(() => {
  if (campaignType.value === "free_card") {
    return freecardValue.value;
  } else {
    return percentValue.value;
  }
});

const resetError = () => {
  isCodeInputError.value = false;
  noCodeError.value = "";
}
const createCampaign = async () => {
try{
  resetError()
  if (!campaignexpire.value) {
    throw "invalid date";
  }
  if(codetext.value === "") {
    isCodeInputError.value = true;
    noCodeError.value = "Please enter a valid code";
    window.scroll(0,0);
    return
  }

  uploading.value = true;
  const code = await promotions.createCampaign(
    codetext.value.toUpperCase(),
    parseInt(campaignValue.value, 10),
    parseInt(campaignlimit.value, 10),
    local2utc(campaignexpire.value, campaignexpiret.value),
    campaignType.value,
    requireLogin.value,
  );
  await promotions.getCampaigns();
  codetext.value = "";
  freecardValue.value = "1";
  percentValue.value = "10";
  campaignexpire.value = inXDays(30);
  campaignlimit.value = "10000";
  uploading.value = false;
  emit("codeCreated", code);
}
catch (e: any) {
  uploading.value = false;
  emit("codeCreationFailed");
}
};

const dateSelected = (date: { detail: { start: string } }) => {
  campaignexpire.value = date.detail?.start;
  dateErrorMessage.value = "";
  isDateInvalid.value = false;
}

const invalidDateEntered = () => {
  dateErrorMessage.value = "Invalid date Entered";
  isDateInvalid.value = true;
}
</script>

<template>
  <h2>Create new campaign</h2>
  <pn-spinner size="2" v-show="uploading" />
  <!-- todo state and position? -->
  <div v-show="!uploading">
    <p v-show="showType">
      <select v-model="campaignType">
        <option value="free_card">Free card</option>
        <option value="percentage">Percentage</option>
      </select>
    </p>
    <p>
      <pn-input
        v-model="codetext"
        label="Code"
        helpertext="Enter Code (mandatory)"
        placeholder="KRAM"
        :invalid = "isCodeInputError"
        :error="noCodeError"
      />
      <!-- todo inputvalidate [A-Z0-9]+ -->
    </p>
    <p>
      <pn-input
        v-model="campaignlimit"
        label="Usage limit"
        helpertext="Number of purchases that can be made in total during this campaign."
        placeholder="10000"
      />
    </p>
    <p>
      <label for="requireLogin">
        <pn-checkbox
          checkboxid="requireLogin"
          :checked="requireLogin"
          @change="requireLogin = $event.target.checked"
        />
        Require login. (If this is unchecked, the code must be kept private)
      </label>
    </p>
    <p>
      <div class="flex align-top">
      <pn-date-picker label-from="Campaign end" :start="campaignexpire" :invalid="isDateInvalid" @dateSelection="dateSelected"  @dateInvalid="invalidDateEntered" :error="dateErrorMessage" class="datePicker"/>
      <div>
        <label class="time-label">Time</label>
        <input class="time" type="time" v-model="campaignexpiret" />
      </div>
      </div>
      <!--eslint-disable-next-line-->
    </p>
    <p v-show="campaignType === 'free_card'">
      <pn-input
        v-model="freecardValue"
        label="Free cards"
        helpertext="Number of free cards each use of the code will give."
        placeholder="1"
      />
    </p>
    <p v-show="campaignType === 'percentage'">
      <pn-input
        v-model="percentValue"
        label="Percent discount"
        helpertext="a number between 1 and 100"
        placeholder="10"
        :error="percentError"
      />
    </p>
    <p>
      <pn-button v-on:click="createCampaign">Create</pn-button>
    </p>
  </div>
</template>


<style lang="scss" scoped>
.time {
    height: 48px;
    padding-bottom: 0;
    margin-left:9px;
}

.datePicker {
  width: 20rem;
}
.time-label { 
  font-size: 0.875em;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    font-weight: 400;
    color: #2d2013;
    margin: 0;
    gap: 0.5em;
    -webkit-tap-highlight-color: transparent;
    transition-property: color;
    transition-duration: 0.2s;
    transition-timing-function: cubic-bezier(0.7, 0, 0.3, 1);
    padding: 1px 0 0 7px;
}
</style>